<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { isArray, BaseNotification, BaseNotificationCall } from "@/shared";

import importedProps from "../config/notificationsProps";
import { useNotificationsStore } from "../model/notificationsStore";

const props = defineProps({ ...importedProps });

const { notify } = storeToRefs(useNotificationsStore());

const notifications = ref([]);
const id = ref(1);
const isAnimation = ref(false);
const timer = ref(null);

const styles = computed(() => {
    const [y, x] = props.position.split(" ");

    return {
        [x]: `${props.xOffset}px`,
        [y]: `${props.yOffset}px`,
        width: `${props.width}px`,
    };
});

const active = computed(() =>
    notifications.value.filter((item) => item.state !== "CLOSED")
);

const animationName = computed(() => {
    const x = props.position.split(" ")[1];

    return x === "right" ? "slide-right" : "slide-left";
});

const notifyColors = {
    attention: "#FAAD65",
    success: "#0AA89E",
    error: "#ff4d4f",
    info: "#1090E4",
};

const addNotify = (notify) => {
    notify.state = "OPENED";

    if (!notify.id) notify.id = id.value++;

    if (!notify.type) notify.type = "attention";

    if (!notify.icon) notify.icon = notify.type;

    if (!notify.title) notify.title = "";

    if (!notify.text) notify.text = "";

    if (!notify.duration) notify.duration = props.defaultDuration;

    if (!notify.date) notify.date = new Date();

    if (!notify.color) notify.color = notifyColors[notify.type];

    if (
        notify.type !== "phone" &&
        props.ignoreDuplicates &&
        active.value.some(
            (item) =>
                item.title === notify.title &&
                item.text === notify.text &&
                item.type === notify.type
        )
    ) {
        return;
    }

    notifications.value.push(notify);
};

const clear = () => {
    if (!isAnimation.value) {
        notifications.value = notifications.value.filter(
            (item) => item.state !== "CLOSED"
        );
    }
};

const handleClose = (notification) => {
    notification.state = "CLOSED";
    isAnimation.value = true;
    clearTimeout(timer.value);
    timer.value = setTimeout(() => {
        isAnimation.value = false;
        clear();
    }, 550);
};

watch(
    () => notify.value,
    (newNotify) => {
        addNotify(newNotify);
    }
);
</script>

<template>
    <div class="base-notifications" :style="styles">
        <TransitionGroup :name="animationName" tag="div">
            <template v-for="notification in active" :key="notification.id">
                <BaseNotificationCall
                    v-if="notification.type === 'phone'"
                    class="base-notifications__item"
                    :item="notification"
                    :type="notification.type"
                    :icon="notification.icon"
                    :duration="15000"
                    :date="notification.date"
                    :text="notification.text"
                    :title="notification.title"
                    :incomingCall="notification.incomingCall"
                    @close="handleClose(notification)"
                />
                <BaseNotification
                    v-else
                    class="base-notifications__item"
                    :color="notification.color"
                    :item="notification"
                    :type="notification.type"
                    :icon="notification.icon"
                    :duration="notification.duration"
                    :date="notification.date"
                    :text="notification.text"
                    :title="notification.title"
                    @close="handleClose"
                >
                    <template
                        v-if="typeof notification.title === 'object'"
                        #title
                    >
                        <component :is="notification.title" />
                    </template>
                    <template
                        v-if="typeof notification.text === 'object'"
                        #text
                    >
                        <template v-if="isArray(notification.text)">
                            <component
                                v-for="(text, index) in notification.text"
                                :key="index"
                                :is="text"
                            />
                        </template>
                        <component v-else :is="notification.text" />
                    </template>
                </BaseNotification>
            </template>
        </TransitionGroup>
    </div>
</template>

<style lang="scss" scoped>
.base-notifications {
    position: fixed;
    z-index: 5555;

    &__item {
        transition: all 0.5s;
    }

    &__item:not(:last-child) {
        margin-bottom: 10px;
    }
}

.slide-right-enter-active {
    animation: slide-in 0.5s;
}

.slide-right-leave-active {
    position: absolute;

    animation: slide-in 0.5s reverse;
}

.slide-left-enter-active {
    animation: slide-left-in 0.5s;
}

.slide-left-leave-active {
    position: absolute;

    animation: slide-left-in 0.5s reverse;
}

@keyframes slide-in {
    0% {
        right: -100%;

        opacity: 0.5;
    }

    100% {
        right: 0;

        opacity: 1;
    }
}

@keyframes slide-left-in {
    0% {
        left: -100%;

        opacity: 0.5;
    }

    100% {
        left: 0;

        opacity: 1;
    }
}
</style>
